import {
  chatServiceUrl,
  flyoutAssetsUrl,
  bucketNameImage,
} from '../../utils/constants'

/** Funding document categories the agent understands (POC funding flow). */
export type ChatDocumentDocType =
  | 'pricing_calculator'
  | 'sow'
  | 'project_plan'
  | 'poc_credit_checklist'
  | 'business_case'
  | 'customer_signoff'
  | 'transcript'
  | 'apid_proof'
  | 'other'

/** A document the user attached to a chat message, already uploaded to S3. */
export interface ChatUploadedDocument {
  filename: string
  s3_uri: string
  doc_type: ChatDocumentDocType
  notes?: string
}

export interface ChatStreamRequestBody {
  user_id: string
  session_id: string
  message: string
  sse_format: string
  agent_type?: string
  documents?: ChatUploadedDocument[]
  /**
   * What the user is looking at when they send. Optional so the request stays
   * valid against a backend that does not read it yet.
   */
  context?: unknown
}

/**
 * Map an `s3://flyout-assets/<key>` upload URI to its public asset URL so the
 * file can be opened in the browser (the raw `s3://` scheme isn't openable).
 * Returns null for any URI outside that bucket so callers can fall back to a
 * non-clickable pill rather than a broken link.
 */
export const flyoutAssetUrlFromS3Uri = (s3Uri: string): string | null => {
  const prefix = `s3://${bucketNameImage}/`
  if (!s3Uri.startsWith(prefix)) return null
  const key = s3Uri.slice(prefix.length)
  if (!key) return null
  // Encode each path segment (keeping the `/` separators) so spaces or reserved
  // characters in a filename don't break the URL.
  const encoded = key.split('/').map(encodeURIComponent).join('/')
  return `${flyoutAssetsUrl}/${encoded}`
}

export const buildChatStreamRequest = (
  partnerId: string,
  body: Omit<ChatStreamRequestBody, 'sse_format'> & { sse_format?: string }
): { url: string; body: ChatStreamRequestBody } => ({
  url: `${chatServiceUrl}/v2/partners/${partnerId}/chat/stream`,
  body: {
    ...body,
    sse_format: body.sse_format ?? 'labra',
    // Omit the key entirely when there's nothing attached.
    agent_type: body.agent_type || undefined,
    documents: body.documents?.length ? body.documents : undefined,
    context: body.context ?? undefined,
  },
})
